import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2 } from "lucide-react";

import { exchangeGoogleAccessToken, exchangeGoogleSession, getGoogleAuthClientConfig } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { applySeoTags } from "@/lib/seo";

const DOMAIN_HINTS = ["jumpcontent.in", "dimerse.com"];
const GOOGLE_IDENTITY_SCRIPT_URL = "https://accounts.google.com/gsi/client";

const SLIDES = [
  {
    id: "rocket-stars",
    quote: "Shoot for the moon. Even if you miss, you'll land among the stars.",
    author: "Norman Vincent Peale",
    gradient: "from-[#1f1b56] via-[#2b2e73] to-[#2c2a66]",
  },
  {
    id: "summit-climber",
    quote: "The summit is what drives us, but the climb itself is what matters.",
    author: "Conrad Anker",
    gradient: "from-[#123a3a] via-[#1f5649] to-[#2f6c57]",
  },
  {
    id: "big-idea",
    quote: "An idea not coupled with action will never get any bigger than the brain cell it occupied.",
    author: "Arnold Glasow",
    gradient: "from-[#151741] via-[#22265b] to-[#34327a]",
  },
  {
    id: "passion-fire",
    quote: "The only way to do great work is to love what you do.",
    author: "Steve Jobs",
    gradient: "from-[#4c1820] via-[#702025] to-[#8f2a2e]",
  },
  {
    id: "navigate-forward",
    quote: "Success is not final, failure is not fatal — it is the courage to continue that counts.",
    author: "Winston Churchill",
    gradient: "from-[#0e3c59] via-[#145075] to-[#186089]",
  },
];

const extractSessionIdFromHash = () => {
  const hashValue = window.location.hash || "";
  if (!hashValue.includes("session_id=")) return "";
  const params = new URLSearchParams(hashValue.replace(/^#/, ""));
  return params.get("session_id") || "";
};

const GoogleIcon = () => (
  <svg aria-hidden="true" width="18" height="18" viewBox="0 0 48 48">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 2.9l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5Z" />
    <path fill="#FF3D00" d="M6.3 14.7 12.9 19c1.8-4.2 6-7 11.1-7 3 0 5.7 1.1 7.8 2.9l5.7-5.7C34.1 6.1 29.3 4 24 4c-7.7 0-14.4 4.4-17.7 10.7Z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.5-5.3l-6.2-5.2C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.3-11.4-8l-6.5 5c3.2 6.4 9.9 11 17.9 11Z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.1 5.5l.1-.1 6.2 5.2c-.4.3 6.5-4.7 6.5-14.6 0-1.2-.1-2.3-.4-3.5Z" />
  </svg>
);

const SlideIllustration = ({ slideId }) => {
  if (slideId === "summit-climber") {
    return (
      <svg viewBox="0 0 360 220" className="h-[250px] w-full" data-testid="auth-hero-scene-svg-summit-climber">
        <defs>
          <linearGradient id="sunrise" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stopColor="#ffd07a" />
            <stop offset="100%" stopColor="#ff8a65" />
          </linearGradient>
        </defs>
        <circle cx="300" cy="52" r="26" fill="url(#sunrise)" opacity="0.9" />
        <polygon points="20,210 120,70 205,210" fill="#1f3f39" />
        <polygon points="120,210 220,60 340,210" fill="#2d5d4e" />
        <rect x="248" y="98" width="6" height="40" fill="#f3f4f6" />
        <polygon points="254,98 277,106 254,114" fill="#fbbf24" />
        <circle cx="240" cy="150" r="7" fill="#f8fafc" />
        <circle cx="248" cy="145" r="4" fill="#f8fafc" />
      </svg>
    );
  }

  if (slideId === "big-idea") {
    return (
      <svg viewBox="0 0 360 220" className="h-[250px] w-full" data-testid="auth-hero-scene-svg-big-idea">
        <circle cx="180" cy="88" r="50" fill="#f59e0b" opacity="0.95" />
        <rect x="160" y="135" width="40" height="36" rx="8" fill="#fbbf24" />
        <line x1="180" y1="20" x2="180" y2="40" stroke="#fde68a" strokeWidth="4" />
        <line x1="115" y1="48" x2="130" y2="62" stroke="#fde68a" strokeWidth="4" />
        <line x1="245" y1="48" x2="230" y2="62" stroke="#fde68a" strokeWidth="4" />
        <circle cx="90" cy="150" r="12" fill="#a78bfa" />
        <circle cx="260" cy="150" r="12" fill="#60a5fa" />
        <path d="M85,150 L120,120 L145,136" stroke="#c4b5fd" strokeWidth="4" fill="none" />
        <path d="M275,150 L240,122 L214,136" stroke="#93c5fd" strokeWidth="4" fill="none" />
      </svg>
    );
  }

  if (slideId === "passion-fire") {
    return (
      <svg viewBox="0 0 360 220" className="h-[250px] w-full" data-testid="auth-hero-scene-svg-passion-fire">
        <rect x="172" y="118" width="16" height="78" rx="8" fill="#fcd34d" />
        <path d="M180 42 C220 76, 214 126, 180 142 C146 126, 140 76, 180 42Z" fill="#fb923c" />
        <path d="M180 66 C206 90, 202 120, 180 132 C158 120, 154 90, 180 66Z" fill="#fef08a" />
        <circle cx="112" cy="172" r="18" fill="#f8fafc" opacity="0.45" />
        <circle cx="248" cy="172" r="18" fill="#f8fafc" opacity="0.45" />
        <circle cx="130" cy="88" r="4" fill="#fdba74" />
        <circle cx="236" cy="70" r="5" fill="#fdba74" />
        <circle cx="252" cy="102" r="3" fill="#fdba74" />
      </svg>
    );
  }

  if (slideId === "navigate-forward") {
    return (
      <svg viewBox="0 0 360 220" className="h-[250px] w-full" data-testid="auth-hero-scene-svg-navigate-forward">
        <circle cx="180" cy="108" r="72" fill="#1e3a8a" opacity="0.55" />
        <circle cx="180" cy="108" r="52" fill="#e2e8f0" />
        <circle cx="180" cy="108" r="34" fill="#cbd5e1" />
        <polygon points="180,64 196,108 180,152 164,108" fill="#f59e0b" />
        <polygon points="180,84 190,108 180,132 170,108" fill="#fbbf24" />
        <path d="M40,186 C95,158 145,206 206,178 C250,160 300,178 330,172" stroke="#7dd3fc" strokeWidth="6" fill="none" opacity="0.8" />
        <circle cx="74" cy="42" r="3" fill="#f8fafc" />
        <circle cx="110" cy="30" r="2" fill="#f8fafc" />
        <circle cx="288" cy="44" r="3" fill="#f8fafc" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 360 220" className="h-[250px] w-full" data-testid="auth-hero-scene-svg-rocket-stars">
      <circle cx="70" cy="34" r="3" fill="#f8fafc" />
      <circle cx="124" cy="50" r="2" fill="#f8fafc" />
      <circle cx="308" cy="36" r="3" fill="#f8fafc" />
      <polygon points="24,210 78,142 140,210" fill="#1f306d" opacity="0.9" />
      <polygon points="120,210 204,102 302,210" fill="#2f4186" opacity="0.85" />
      <g transform="translate(182 92) rotate(-28)">
        <ellipse cx="0" cy="0" rx="36" ry="18" fill="#f8fafc" />
        <circle cx="8" cy="0" r="6" fill="#60a5fa" />
        <polygon points="-28,0 -50,14 -42,0 -50,-14" fill="#ef4444" />
        <polygon points="20,0 34,8 34,-8" fill="#f59e0b" />
      </g>
      <path d="M230,128 C252,136 272,150 286,170" stroke="#fbbf24" strokeWidth="6" strokeLinecap="round" opacity="0.7" />
    </svg>
  );
};

export default function AuthPage({ onAuthSuccess }) {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isExchangingSession, setIsExchangingSession] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [googleClientConfig, setGoogleClientConfig] = useState({});
  const [isGoogleConfigLoading, setIsGoogleConfigLoading] = useState(true);
  const [googleConfigError, setGoogleConfigError] = useState("");
  const [selectedDomain, setSelectedDomain] = useState("dimerse.com");
  const [isGoogleScriptReady, setIsGoogleScriptReady] = useState(false);
  const processedSessionRef = useRef(false);

  const resolveGoogleConfigErrorMessage = useCallback((error) => {
    const raw = String(error?.message || "").trim();
    const normalized = raw.toLowerCase();
    if (!raw) {
      return "Unable to load Google login configuration right now. Please try again in a moment.";
    }
    if (
      normalized.includes("502")
      || normalized.includes("bad gateway")
      || normalized.includes("network")
      || normalized.includes("failed to fetch")
      || normalized.includes("temporarily unavailable")
      || normalized.includes("cloudflare")
      || normalized.includes("origin web server")
      || normalized.includes("overloaded")
      || normalized.includes("misconfigured")
    ) {
      return "Google login service is temporarily unreachable. Please try again in a moment.";
    }
    return raw;
  }, []);

  const fetchGoogleClientConfig = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setIsGoogleConfigLoading(true);
    }
    setGoogleConfigError("");

    try {
      let data = null;
      let lastError = null;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          data = await getGoogleAuthClientConfig();
          break;
        } catch (error) {
          lastError = error;
          if (attempt === 1) {
            throw error;
          }
          await new Promise((resolve) => {
            window.setTimeout(resolve, 900);
          });
        }
      }
      if (!data && lastError) {
        throw lastError;
      }

      const nextClients = data?.clients || {};
      setGoogleClientConfig(nextClients);
      const nextDomains = Object.keys(nextClients);
      if (!nextDomains.length) {
        const message = "Google login is not configured yet for this environment.";
        setGoogleConfigError(message);
        setLoginError(message);
        return {};
      }

      setSelectedDomain((prev) => (nextClients[prev] ? prev : nextDomains[0]));
      setLoginError("");
      return nextClients;
    } catch (error) {
      const message = resolveGoogleConfigErrorMessage(error);
      setGoogleClientConfig({});
      setGoogleConfigError(message);
      setLoginError(message);
      return {};
    } finally {
      setIsGoogleConfigLoading(false);
    }
  }, [resolveGoogleConfigErrorMessage]);

  useEffect(() => {
    applySeoTags({
      title: "Dimerse Dashboard",
      description: "Dimerse dashboard for client collaboration, delivery visibility, and execution management.",
      canonicalPath: "/",
      imageUrl: `${window.location.origin}/og/dashboard-share.png`,
    });
  }, []);

  useEffect(() => {
    const timerId = window.setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % SLIDES.length);
    }, 6000);
    return () => clearInterval(timerId);
  }, []);

  useEffect(() => {
    fetchGoogleClientConfig();
  }, [fetchGoogleClientConfig]);

  useEffect(() => {
    let cancelled = false;

    const markReady = () => {
      if (cancelled) return;
      if (window.google?.accounts?.oauth2) {
        setIsGoogleScriptReady(true);
      }
    };

    if (window.google?.accounts?.oauth2) {
      markReady();
      return () => {
        cancelled = true;
      };
    }

    const existingScript = document.querySelector(`script[src="${GOOGLE_IDENTITY_SCRIPT_URL}"]`);
    if (existingScript) {
      existingScript.addEventListener("load", markReady);
      existingScript.addEventListener("error", () => {
        if (!cancelled) setLoginError("Unable to load Google Sign-In. Please refresh and try again.");
      });
      return () => {
        cancelled = true;
      };
    }

    const script = document.createElement("script");
    script.src = GOOGLE_IDENTITY_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = markReady;
    script.onerror = () => {
      if (!cancelled) setLoginError("Unable to load Google Sign-In. Please refresh and try again.");
    };
    document.head.appendChild(script);

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const sessionId = extractSessionIdFromHash();
    if (!sessionId || processedSessionRef.current) return;

    processedSessionRef.current = true;
    setIsExchangingSession(true);
    setLoginError("");

    const exchangeSession = async () => {
      try {
        const session = await exchangeGoogleSession({ session_id: sessionId });
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
        onAuthSuccess(session);
      } catch (error) {
        const message = error?.message || "Unable to sign in with Google. Please try another account.";
        setLoginError(message);
        toast.error(message);
      } finally {
        setIsExchangingSession(false);
      }
    };

    exchangeSession();
  }, [onAuthSuccess]);

  const handleGoogleLogin = async () => {
    setLoginError("");
    setIsRedirecting(true);

    if (isGoogleConfigLoading) {
      setIsRedirecting(false);
      const message = "Google login configuration is still loading. Please try again in a moment.";
      setLoginError(message);
      toast.error(message);
      return;
    }

    let configMap = googleClientConfig;
    if (!Object.keys(configMap).length) {
      configMap = await fetchGoogleClientConfig({ silent: true });
    }

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const resolvedDomain = configMap[selectedDomain] ? selectedDomain : Object.keys(configMap)[0];
    const clientId = resolvedDomain ? configMap[resolvedDomain] : "";
    if (!clientId) {
      setIsRedirecting(false);
      const message = googleConfigError || "Google login is not configured yet for this environment.";
      setLoginError(message);
      toast.error(message);
      return;
    }

    if (!isGoogleScriptReady || !window.google?.accounts?.oauth2) {
      setIsRedirecting(false);
      const message = "Google Sign-In is still loading. Please try again in a moment.";
      setLoginError(message);
      toast.error(message);
      return;
    }

    try {
      const accessToken = await new Promise((resolve, reject) => {
        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: "openid email profile",
          callback: (tokenResponse) => {
            if (tokenResponse?.error) {
              reject(new Error(tokenResponse?.error_description || tokenResponse?.error || "Google authentication was cancelled"));
              return;
            }
            if (!tokenResponse?.access_token) {
              reject(new Error("Google authentication did not return an access token"));
              return;
            }
            resolve(tokenResponse.access_token);
          },
        });

        tokenClient.requestAccessToken({ prompt: "consent" });
      });

      const session = await exchangeGoogleAccessToken({
        access_token: accessToken,
        domain_hint: resolvedDomain,
      });
      onAuthSuccess(session);
    } catch (error) {
      const message = error?.message || "Unable to sign in with Google. Please try another account.";
      setLoginError(message);
      toast.error(message);
    } finally {
      setIsRedirecting(false);
    }
  };

  const slide = SLIDES[activeSlide];

  return (
    <main className="min-h-screen bg-[#f6f6f7] p-0" data-testid="auth-page-root">
      <div className="grid min-h-screen grid-cols-1 lg:grid-cols-[390px_1fr]" data-testid="auth-layout-grid">
        <section className="relative flex flex-col border-r border-slate-200 bg-[#f6f6f7] px-8 py-10" data-testid="auth-left-panel">
          <div>
            <h1 className="text-[48px] leading-none font-semibold tracking-tight text-slate-900" data-testid="auth-brand-name">
              Dimerse
            </h1>
            <p className="mt-1 text-sm text-slate-400" data-testid="auth-brand-tagline">Work Digitally · Connect Ahead</p>
          </div>

          <div className="mt-14" data-testid="auth-copy-wrap">
            <h2 className="text-5xl font-semibold leading-[1.05] text-slate-900" data-testid="auth-heading">
              Good to see you.
            </h2>
            <p className="mt-5 text-xl leading-8 text-slate-500" data-testid="auth-subheading">
              Sign in with your company Google account. Only <strong>@dimerse.com</strong> and <strong>@jumpcontent.in</strong> are allowed.
            </p>
          </div>

          <p className="mt-10 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500" data-testid="auth-common-software-note">
            Both organizations use one common Agency Workspace.
          </p>

          <Button
            type="button"
            variant="outline"
            className="mt-6 h-14 justify-start gap-3 rounded-2xl border-slate-300 bg-white px-5 text-base font-semibold text-slate-900 hover:bg-slate-50"
            disabled={isRedirecting || isExchangingSession || isGoogleConfigLoading}
            onClick={handleGoogleLogin}
            data-testid="google-login-button"
          >
            <GoogleIcon />
            {isGoogleConfigLoading ? "Loading Google login..." : (isRedirecting || isExchangingSession ? "Connecting to Google..." : "Continue with Google")}
          </Button>

          {isGoogleConfigLoading ? (
            <p className="mt-2 text-xs text-slate-500" data-testid="google-config-loading-note">
              Verifying Google login configuration...
            </p>
          ) : null}

          <div className="mt-3 flex items-center gap-2" data-testid="google-domain-selector">
            {DOMAIN_HINTS.map((domain) => (
              <button
                key={domain}
                type="button"
                onClick={() => setSelectedDomain(domain)}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${selectedDomain === domain ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}
                data-testid={`google-domain-chip-${domain.replace(/\./g, "-")}`}
              >
                @{domain}
              </button>
            ))}
          </div>

          {loginError ? (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4" data-testid="google-login-error-card">
              <p className="text-sm font-semibold text-rose-800" data-testid="google-login-error-title">Access denied.</p>
              <p className="mt-1 text-sm text-rose-700" data-testid="google-login-error-message">{loginError}</p>
              <button
                type="button"
                onClick={handleGoogleLogin}
                className="mt-2 text-sm font-semibold text-rose-700 underline"
                data-testid="google-login-try-different-account-button"
              >
                Try a different account →
              </button>
            </div>
          ) : null}

          <div className="mt-10 border-t border-slate-200 pt-5" data-testid="auth-enterprise-strip">
            <p className="text-center text-xs uppercase tracking-[0.08em] text-slate-400" data-testid="auth-enterprise-label">enterprise only</p>
            <div className="mt-2 flex items-center justify-center gap-4 text-sm text-slate-500" data-testid="auth-domain-bullets">
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" />jumpcontent.in</span>
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-indigo-500" />dimerse.com</span>
            </div>
          </div>

          <p className="mt-auto pt-10 text-sm text-slate-400" data-testid="auth-contact-help">
            Need access? Contact <a href="mailto:admin@dimerse.com" className="font-medium text-indigo-700 underline" data-testid="auth-contact-email">admin@dimerse.com</a>
          </p>
        </section>

        <section className={`relative hidden overflow-hidden bg-gradient-to-br ${slide.gradient} px-12 py-14 lg:flex lg:flex-col`} data-testid="auth-right-panel">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,.14),transparent_40%)]" />
          <div className="relative z-10 mx-auto mt-16 w-[420px] rounded-3xl border border-white/20 bg-black/10 p-6 shadow-2xl backdrop-blur-sm" data-testid={`auth-hero-illustration-card-${slide.id}`}>
            <SlideIllustration slideId={slide.id} />
          </div>

          <div className="relative z-10 mx-auto mt-12 max-w-3xl text-center" data-testid="auth-quote-container">
            <div className="mb-3 text-2xl tracking-[0.3em] text-white/70" data-testid="auth-quote-stars">✦✦✦</div>
            <p className="text-4xl font-semibold leading-[1.2] text-white" data-testid="auth-quote-text">
              {slide.quote}
            </p>
            <p className="mt-3 text-sm tracking-[0.08em] text-white/70" data-testid="auth-quote-author">— {slide.author}</p>
          </div>

          <div className="relative z-10 mt-auto flex items-center justify-center gap-2" data-testid="auth-quote-dots">
            {SLIDES.map((item, index) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setActiveSlide(index)}
                className={`h-2 rounded-full transition-all ${index === activeSlide ? "w-8 bg-white" : "w-2 bg-white/35"}`}
                data-testid={`auth-quote-dot-${index}`}
                aria-label={`Show quote ${index + 1}`}
              />
            ))}
          </div>

          <div className="relative z-10 mt-6 flex items-center justify-center gap-2 text-sm text-white/70" data-testid="auth-allowed-domains-footer">
            <CheckCircle2 className="h-4 w-4 text-emerald-300" />
            {DOMAIN_HINTS.map((domain) => `@${domain}`).join(" · ")}
          </div>
        </section>
      </div>
    </main>
  );
}
